import React, { useState, useEffect } from 'react';
import {
  green,
  white,
  sand,
  mint,
  lightGreen,
  accent,
  subheadline,
} from '@/shared/styles/colors';
import { BodyText, H4, H3, BaseText } from '@/shared/components/StyledText';
import styled from '@emotion/native';
import {
  Consultation,
  ConsultationStatus,
  MutationResult,
  SessionType,
  User,
} from '@/shared/types/generated';
import { format, isToday, isWithinInterval } from 'date-fns';
import { TouchableOpacity, View, ImageBackground } from 'react-native';
import { formatCurrency, getStylistRate } from '@/shared/utils';
import Spacer from '@/shared/components/Spacer';
import StylistProfileModal from '../stylist-profile/StylistProfileModal';
import SessionDetailModal from './SessionDetailModal';
import ActionButtons from './ActionButtons';
import StatusBadge from './StatusBadge';
import { InPersonAddressDisplay } from './PendingRequestAddress';
import PendingRequestActions, {
  MobileTopButtons,
} from './PendingRequestActions';
import useStore from '@/stores/useStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import ClientProfileModal from '@/shared/components/ClientProfileModal';
import BookingModal from '../bookConsultation/BookingModal';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';
import useDimensions from '@/shared/hooks/useDimensions';
import { useJoinCall } from '@/shared/hooks/useJoinCall';
import ViewPrivateNotesLink from '@/shared/components/ViewPrivateNotesLink';
import {
  isInPersonAtEvent,
  isInPersonSession,
} from '@/shared/utils/sessionUtils';
import InPersonCompletionModal from './InPersonCompletionModal';
import { useMutation } from '@apollo/client';
import {
  APPROVE_IN_PERSON_REQUEST,
  DECLINE_IN_PERSON_REQUEST,
} from '@/shared/mutations';

const DESKTOP_IMAGE_WIDTH = 86;
const HORIZONTAL_PADDING = 20;

// Helper function to check if the current time is within the session time
function isJoinable(startTime: Date, endTime: Date): boolean {
  return (
    isToday(startTime) &&
    isWithinInterval(new Date(), { start: startTime, end: endTime })
  );
}

const SessionCard = ({
  consultation,
  autoOpenDetail = false,
  onDetailModalClose,
  onRequestHandled,
}: {
  consultation: Consultation;
  autoOpenDetail?: boolean;
  onDetailModalClose?: () => void;
  onRequestHandled?: () => void;
}) => {
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(autoOpenDetail);
  const [
    isInPersonCompletionModalVisible,
    setIsInPersonCompletionModalVisible,
  ] = useState(false);
  const { startTime, endTime, status } = consultation;
  const { isStylist, setSnackbarState } = useStore();
  const { isDesktop } = useBreakpoint();
  const { width: screenWidth } = useDimensions();
  const { navigateToChannel } = useChannelRouting();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { handleJoinCall, isJoining } = useJoinCall({ consultation });

  const inPersonAtEvent = isInPersonAtEvent(consultation);
  const inPersonSession = isInPersonSession(consultation);

  // Check if this is a pending in-person request
  const isPendingInPersonRequest =
    status === ConsultationStatus.Pending &&
    consultation.sessionType === SessionType.InPersonSession;

  // Get client address for pending in-person requests
  const clientAddress = consultation.client?.user?.address;

  // Mutations for accepting/declining requests
  const [approveRequest, { loading: approving }] = useMutation<
    MutationResult<'approveInPersonRequest'>
  >(APPROVE_IN_PERSON_REQUEST);
  const [declineRequest, { loading: declining }] = useMutation<
    MutationResult<'declineInPersonRequest'>
  >(DECLINE_IN_PERSON_REQUEST);

  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false);
  const [shouldShowBookingModal, setShouldShowBookingModal] = useState(false);
  // Maintain `canJoin` in state and refresh every ~15 seconds
  const [canJoin, setCanJoin] = useState(false);
  const isSessionToday = isToday(startTime);

  useEffect(() => {
    function computeCanJoin() {
      return (
        !inPersonAtEvent &&
        !inPersonSession &&
        isJoinable(startTime, endTime) &&
        (status === ConsultationStatus.InProgress ||
          status === ConsultationStatus.Upcoming)
      );
    }

    setCanJoin(computeCanJoin());
    const intervalId = setInterval(() => {
      setCanJoin(computeCanJoin());
    }, 5000);
    return () => {
      clearInterval(intervalId);
    };
  }, [inPersonAtEvent, inPersonSession, startTime, endTime, status]);

  // Calculate stylist image height to match container width, to create a square
  const stylistImageHeight = isDesktop
    ? DESKTOP_IMAGE_WIDTH
    : screenWidth - HORIZONTAL_PADDING * 2;

  const stylistRate = getStylistRate(consultation);

  // Auto-open detail modal when autoOpenDetail prop changes
  useEffect(() => {
    if (autoOpenDetail) {
      setDetailModalVisible(true);
    }
  }, [autoOpenDetail]);

  const handleViewDetails = () => {
    setDetailModalVisible(true);
  };

  const handleGoToChat = async () => {
    if (!consultation.stylist?.user?.id || !consultation.client?.user?.id) {
      return;
    }
    const channelUrl = await fetchChannelUrl(
      consultation.stylist?.user?.id,
      consultation.client?.user?.id,
    );
    if (channelUrl) {
      navigateToChannel(channelUrl);
    }
  };

  const handleViewProfile = () => {
    setIsProfileModalVisible(true);
  };

  const handleEndSession = () => {
    setIsInPersonCompletionModalVisible(true);
  };

  const handleAcceptRequest = async () => {
    try {
      await approveRequest({
        variables: {
          input: { consultationId: consultation.id },
        },
      });
      setSnackbarState({
        open: true,
        message: `Request from ${consultation.client?.user?.firstName} accepted`,
        severity: 'success',
      });
      onRequestHandled?.();
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Failed to accept request. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleDeclineRequest = async () => {
    try {
      await declineRequest({
        variables: {
          input: { consultationId: consultation.id },
        },
      });
      setSnackbarState({
        open: true,
        message: `Request from ${consultation.client?.user?.firstName} declined`,
        severity: 'info',
      });
      onRequestHandled?.();
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Failed to decline request. Please try again.',
        severity: 'error',
      });
    }
  };

  let otherUser: User | undefined | null;

  if (isStylist) {
    otherUser = consultation.client?.user;
  } else {
    otherUser = consultation.stylist?.user;
  }

  const showItalicText = !canJoin && status === ConsultationStatus.Upcoming;
  const showInPersonText =
    inPersonAtEvent && status === ConsultationStatus.Upcoming;

  const onConsultationButtonPress = () => {
    setShouldShowBookingModal(true);
    setIsProfileModalVisible(false);
  };

  const onStylistProfileModalDismiss = () => {
    if (shouldShowBookingModal) {
      setShouldShowBookingModal(false);
      setIsBookingModalVisible(true);
    }
  };

  return (
    <SessionCardContainer isDesktop={isDesktop}>
      <PaddingView isDesktop={isDesktop}>
        <>
          {isPendingInPersonRequest ? (
            <PendingBadge isDesktop={isDesktop}>Pending to Accept</PendingBadge>
          ) : (
            <StatusBadge
              status={status}
              isSessionToday={isSessionToday}
              canJoin={canJoin}
            />
          )}
          <SessionDate>
            {format(startTime, 'EEEE')} | {format(startTime, 'MM/dd/yy')}{' '}
            {isDesktop && '| '}
          </SessionDate>
          <SessionInfoRow isDesktop={isDesktop}>
            <SessionInfo>
              <SessionDate>
                {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
              </SessionDate>
              {inPersonSession && <SessionDate>| In-Person</SessionDate>}
            </SessionInfo>
          </SessionInfoRow>
        </>
        {/* Desktop: Accept/Decline buttons and italic text for pending requests */}
        {isPendingInPersonRequest && isDesktop && (
          <DesktopRequestActions>
            <ItalicText isDesktop={isDesktop} style={{ marginRight: 16 }}>
              Use chat to confirm any final details.
            </ItalicText>
          </DesktopRequestActions>
        )}
        {!isPendingInPersonRequest &&
          !inPersonAtEvent &&
          showItalicText &&
          isDesktop &&
          !inPersonSession && (
            <ItalicText isDesktop={isDesktop} style={{ marginRight: 0 }}>
              Come back on the day & time of the meeting to join
            </ItalicText>
          )}
        {!isPendingInPersonRequest &&
          inPersonAtEvent &&
          isStylist &&
          isDesktop && (
            <ItalicText isDesktop={isDesktop} style={{ marginRight: 0 }}>
              Coordinate meeting spot with client
            </ItalicText>
          )}
      </PaddingView>
      {/* Mobile: Accept/Decline buttons for pending requests */}
      {isPendingInPersonRequest && !isDesktop && (
        <MobileTopButtons
          onAccept={handleAcceptRequest}
          onDecline={handleDeclineRequest}
          approving={approving}
          declining={declining}
        />
      )}
      <Spacer height={2} />
      <StylistCard isDesktop={isDesktop}>
        <StylistImageContainer isDesktop={isDesktop}>
          <StylistImage
            source={{
              uri: otherUser?.profilePicUrl,
            }}
            imageStyle={{
              resizeMode: 'cover',
            }}
            height={stylistImageHeight}
          />
        </StylistImageContainer>
        <DesktopNameContainer isDesktop={isDesktop}>
          <StylistName isDesktop={isDesktop}>
            {otherUser?.firstName} {otherUser?.lastName}
          </StylistName>
          <ViewProfileContainer isDesktop={isDesktop}>
            {!isStylist && stylistRate && (
              <HourlyRate>{formatCurrency(stylistRate)}/hour | </HourlyRate>
            )}
            <TouchableOpacity onPress={handleViewProfile}>
              <ProfileText>View Profile</ProfileText>
            </TouchableOpacity>
            {isStylist && consultation.client?.id && (
              <ViewPrivateNotesLink
                clientId={consultation.client.id}
                fontSize={12}
                style={{ marginLeft: 16 }}
                showUnderline={true}
              />
            )}
          </ViewProfileContainer>
        </DesktopNameContainer>
        {!isPendingInPersonRequest &&
          !showInPersonText &&
          showItalicText &&
          !isDesktop &&
          !inPersonSession && (
            <ItalicAddressText>
              Come back on the day & time of the meeting to join
            </ItalicAddressText>
          )}
        {/* Address display for in-person sessions on mobile */}
        {!isDesktop && inPersonSession && (
          <InPersonAddressDisplay
            address={clientAddress}
            isPending={isPendingInPersonRequest}
            isStylist={!!isStylist}
          />
        )}
        {isDesktop && <View style={{ height: 10, flexGrow: 1 }} />}
        {/* Buttons for pending in-person requests */}
        {isPendingInPersonRequest ? (
          <PendingRequestActions
            onAccept={handleAcceptRequest}
            onDecline={handleDeclineRequest}
            onViewDetails={handleViewDetails}
            approving={approving}
            declining={declining}
          />
        ) : (
          <ActionButtons
            canJoin={inPersonAtEvent || inPersonSession ? false : canJoin}
            handleJoinCall={handleJoinCall}
            handleGoToChat={handleGoToChat}
            handleViewDetails={handleViewDetails}
            showDetails
            isJoining={inPersonAtEvent || inPersonSession ? false : isJoining}
            isChatButtonPrimaryStyle={
              !isStylist && (inPersonAtEvent || inPersonSession) ? true : false
            }
            showEndButton={
              (inPersonAtEvent || inPersonSession) &&
              !!isStylist &&
              status === ConsultationStatus.InProgress
            }
            handleEndSession={handleEndSession}
          />
        )}

        <Spacer height={7} />
      </StylistCard>
      {!isStylist && (
        <StylistProfileModal
          id={consultation.stylistId}
          isVisible={isProfileModalVisible}
          setIsVisible={setIsProfileModalVisible}
          showConsultationButton
          onConsultationButtonPress={onConsultationButtonPress}
          onDismiss={onStylistProfileModalDismiss}
        />
      )}
      {!isStylist && (
        <BookingModal
          isVisible={isBookingModalVisible}
          setIsVisible={setIsBookingModalVisible}
        />
      )}
      {isStylist && (
        <ClientProfileModal
          clientId={consultation.clientId}
          isVisible={isProfileModalVisible}
          setIsVisible={setIsProfileModalVisible}
        />
      )}
      <SessionDetailModal
        consultation={consultation}
        isVisible={detailModalVisible}
        setIsVisible={(visible) => {
          setDetailModalVisible(visible);
          if (!visible && onDetailModalClose) {
            onDetailModalClose();
          }
        }}
        isSessionToday={isSessionToday}
        isJoinable={canJoin}
        openProfileModal={() => setIsProfileModalVisible(true)}
        onRequestHandled={onRequestHandled}
      />
      {(inPersonAtEvent || inPersonSession) && isStylist && (
        <InPersonCompletionModal
          isVisible={isInPersonCompletionModalVisible}
          setIsVisible={setIsInPersonCompletionModalVisible}
          consultation={consultation}
        />
      )}
    </SessionCardContainer>
  );
};

const SessionCardContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    paddingTop: isDesktop ? 15 : 20,
    backgroundColor: mint,
    borderRadius: 8,
    marginBottom: isDesktop ? 19 : 24,
    borderWidth: isDesktop ? 1 : 0,
    borderColor: mint,
    ...BOX_SHADOW_STYLE,
  }),
);

const PaddingView = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  paddingHorizontal: HORIZONTAL_PADDING,
  flexDirection: isDesktop ? 'row' : 'column',
}));

const SessionDate = styled(H3)({
  marginBottom: 0,
  textTransform: 'uppercase',
  fontSize: 18,
  letterSpacing: -0.18,
  fontWeight: 'regular',
  lineHeight: 21,
});

const SessionInfoRow = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: isDesktop ? 15 : 19,
}));

const SessionInfo = styled.View({
  gap: 4,
  justifyContent: 'flex-end',
  flexDirection: 'row',
});

const StylistCard = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  backgroundColor: white,
  borderRadius: 8,
  flexDirection: isDesktop ? 'row' : 'column',
  padding: isDesktop ? 16 : 0,
  paddingBottom: 0,
  paddingRight: isDesktop ? 27 : 0,
  borderTopLeftRadius: isDesktop ? 0 : 8,
  borderTopRightRadius: isDesktop ? 0 : 8,
}));

const StylistImageContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    width: isDesktop ? DESKTOP_IMAGE_WIDTH : '100%',
    height: isDesktop ? 52 : 202,
    backgroundColor: sand,
    marginBottom: isDesktop ? 0 : 11,
    marginRight: isDesktop ? 21 : 0,
    borderRadius: isDesktop ? 8 : 0,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: 'hidden',
  }),
);

const StylistImage = styled(ImageBackground)<{ height: number }>(
  ({ height }) => ({
    width: '100%',
    height,
    top: 0,
    position: 'absolute',
  }),
);

const StylistName = styled(H4)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  textAlign: 'center',
  marginBottom: isDesktop ? 2 : 3,
  textTransform: 'uppercase',
  color: green,
}));

const HourlyRate = styled(BodyText)({
  color: green,
  textAlign: 'center',
  fontSize: 14,
  lineHeight: 18,
});

const LinkText = styled(BodyText)({
  color: green,
  textDecorationLine: 'underline',
  textAlign: 'center',
});

const ViewProfileContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    width: isDesktop ? 'auto' : '100%',
    justifyContent: 'center',
    marginBottom: 15,
  }),
);

const ProfileText = styled(LinkText)({
  textDecorationLine: 'underline',
  fontSize: 12,
  fontWeight: 'bold',
  color: green,
});

const ItalicText = styled(BodyText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    fontStyle: 'italic',
    lineHeight: 16,
    fontSize: 14,
    textAlign: isDesktop ? 'right' : 'center',
    marginBottom: isDesktop ? 0 : 18,
    maxWidth: isDesktop ? 'auto' : 181,
    marginHorizontal: isDesktop ? 'auto' : 'auto',
    color: lightGreen,
    textWrap: 'no-wrap',
  }),
);

const DesktopNameContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'column',
    alignItems: isDesktop ? 'flex-start' : 'center',
    justifyContent: 'center',
  }),
);

// Pending request styles
const PendingBadge = styled(BaseText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    backgroundColor: accent,
    color: green,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    alignSelf: 'flex-start',
    letterSpacing: -0.12,
    marginBottom: 4,
    marginRight: isDesktop ? 12 : 0,
  }),
);

const DesktopRequestActions = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginLeft: 'auto',
});

const ItalicAddressText = styled(BaseText)({
  fontStyle: 'italic',
  fontSize: 14,
  color: subheadline,
  textAlign: 'center',
  marginBottom: 16,
});

export default SessionCard;
